
import ErrorModal from "@/components/modal/Error.vue";
import StepBar from "@/components/stepBar/StepBar.vue";
import { Inscription, InscriptionStatus } from "@/types/interface";
import axios from "axios";
import { defineComponent } from "vue";

export interface Category {
  id: number;
  name: string;
  maxTeamMembers: number;
  minTeamMembers: number;
}

export interface Race {
  id: number;
  name: string;
  registrationPrice: number;
  vaRegistrationPrice: number;
  category: Category;
  maxTeams: number;
  inscriptions: Inscription[];
  teams: Team[];
  maxParticipants: number;
  disciplines: RaceDiscipline[];
}

export interface TeamRace {
  id: number;
  name: string;
}

export interface Team {
  id: number;
  name: string;
  race: Race;
  members: Inscription[];
}

export interface RaceDiscipline {
  discipline: Discipline;
  duration: number;
  id: number;
}

export interface Discipline {
  id: number;
  name: string;
}

export default defineComponent({
  components: {
    StepBar,
    ErrorModal,
  },
  data() {
    return {
      solo: false,
      team: false,
      joinTeam: false,
      createTeam: false,
      selectedRace: null,
      joinedTeamId: null,
      joinedTeamPassword: null,
      createdTeamName: null,
      createdTeamRace: null,
      createdTeamCategory: null,
      createdTeamPassword: null,
      createdTeamConfirmPassword: null,
      selectedCategory: null,
      teams: [] as Team[],
      races: [] as Race[],
      soloRaces: [] as Race[],
      categories: [] as Category[],
      showErrorModal: false,
    };
  },
  methods: {
    toggleErrorModal() {
      this.showErrorModal = !this.showErrorModal;
    },
    centimesToEuros(centimes: number): number {
      return centimes / 100;
    },
    soloOrTeam(event: Event) {
      if (event) {
        if (
          event.target &&
          (event.target as HTMLInputElement).id === "solo" &&
          (event.target as HTMLInputElement).checked === true
        ) {
          this.solo = true;
          this.team = false;
        } else {
          this.solo = false;
          this.team = true;
        }
        this.joinTeam = false;
        this.createTeam = false;
      }
    },
    getSoloVaPrice(): number {
      let vaPrice = 0;
      if (this.selectedRace) {
        this.soloRaces.forEach((race) => {
          if (race.id === this.selectedRace) {
            vaPrice = this.centimesToEuros(race.vaRegistrationPrice);
          }
        });
      }
      return vaPrice;
    },
    getSoloRegularPrice(): number {
      let price = 0;
      if (this.selectedRace) {
        this.soloRaces.forEach((race) => {
          if (race.id === this.selectedRace) {
            price = this.centimesToEuros(race.registrationPrice);
          }
        });
      }
      return price;
    },
    getVaPrice(): number {
      let vaPrice = 0;
      if (this.createdTeamRace) {
        this.races.forEach((race) => {
          if (race.id === this.createdTeamRace) {
            vaPrice = this.centimesToEuros(race.vaRegistrationPrice);
          }
        });
      }
      return vaPrice;
    },
    getRegularPrice(): number {
      let price = 0;
      if (this.createdTeamRace) {
        this.races.forEach((race) => {
          if (race.id === this.createdTeamRace) {
            price = this.centimesToEuros(race.registrationPrice);
          }
        });
      }
      return price;
    },
    joinOrCreateTeam(event: Event) {
      if (event) {
        if (
          event.target &&
          (event.target as HTMLInputElement).id === "createTeam" &&
          (event.target as HTMLInputElement).checked === true
        ) {
          this.createTeam = true;
          this.joinTeam = false;
        } else {
          this.createTeam = false;
          this.joinTeam = true;
        }
        this.createdTeamCategory = null;
      }
    },
    raceSoloIsComplete(id: number) {
      let race = this.soloRaces?.find((r: Race) => r.id === id);
      if (race === undefined) return false;
      const sizeRace = race.inscriptions.filter(
        (i: Inscription) => i.status !== InscriptionStatus.CANCELLED
      ).length;
      return sizeRace >= race.maxParticipants;
    },
    raceIsComplete(id: number) {
      let race = this.races?.find((r: Race) => r.id === id);
      if (race === undefined) return false;
      const sizeRace = race.teams.length;
      return sizeRace >= race.maxTeams;
    },
    teamIsComplete(id: number) {
      const team = this.teams.find((t: Team) => t.id === id);
      const sizeTeam = team?.members.filter(
        (i: Inscription) => i.status !== InscriptionStatus.CANCELLED
      ).length;
      if (sizeTeam !== undefined && team?.race.category.maxTeamMembers)
        return sizeTeam >= team?.race.category.maxTeamMembers;
      else return false;
    },
    async fetchRaces() {
      const racesResponse = await axios.get("races", {
        params: {
          categoryId: this.selectedCategory,
          editionId: this.$store.getters["edition/getEditionId"],
        },
      });
      if (racesResponse.status < 300) {
        this.races = racesResponse.data.data;
      }
    },
    next() {
      this.$store.dispatch("user/setMe");
      this.$router.push({ name: "RegisterVa" });
    },
    async submitCreateTeam() {
      const response = await axios.post("teams", {
        name: this.createdTeamName,
        password: this.createdTeamPassword,
        raceId: this.createdTeamRace,
        editionId: this.$store.getters["edition/getEditionId"],
      });
      if (response.status !== 200) return this.toggleErrorModal();
      this.next();
    },
    async submitJoinTeam() {
      const response = await axios.post(`/teams/${this.joinedTeamId}/join`, {
        password: this.joinedTeamPassword,
      });
      if (response.status !== 201) return this.toggleErrorModal();
      this.next();
    },
    async submitSoloRace() {
      const response = await axios.post("/inscriptions", {
        raceId: this.selectedRace,
        editionId: this.$store.getters["edition/getEditionId"],
      });
      if (response.status !== 201) return this.toggleErrorModal();
      this.next();
    },
  },
  async mounted() {
    this.fetchRaces();
    const categoriesResponse = await axios.get("categories/light", {
      params: {
        editionId: this.$store.getters["edition/getEditionId"],
        maxTeamMembers: 1,
      },
    });
    if (categoriesResponse.status < 300) {
      this.categories = categoriesResponse.data.data;
    }
    const soloRacesResponse = await axios.get("races", {
      params: {
        minTeamMembers: 1,
        editionId: this.$store.getters["edition/getEditionId"],
      },
    });
    if (soloRacesResponse.status < 300) {
      this.soloRaces = soloRacesResponse.data.data;
    }
    const teamsResponse = await axios.get("teams/light", {
      params: {
        editionId: this.$store.getters["edition/getEditionId"],
      },
    });
    if (teamsResponse.status < 300) {
      this.teams = teamsResponse.data.data;
    }
  },
});
